import { useLocalObservable } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useMst } from '../../models/WithWineState';
import { CartActionOption } from '../../types/CartItemProps';
import { ProductCardPropsV2 } from '../../types/ProductCardProps';
import { TypeProduct } from '../../types/TypeProduct';
import { parseBoolean } from '../../utils/parseBoolean';
import { useFallbackImage } from '../../utils/useFallbackImage';
import { useToasterContext } from '../../components/Toaster';

export const useProductActionsState = (props: ProductCardPropsV2) => {
  const {
    enablevariations,
    overrideDecrement,
    overrideIncrement,
    toasteroptions,
    cartaction = CartActionOption.ShowNotifications,
  } = props;

  const stateTree = useMst();
  const { showToast } = useToasterContext();
  const productId = props.product_id || props.product?.id;

  const state = useLocalObservable(() => ({
    get items() {
      return stateTree.session?.items;
    },

    get data() {
      if (productId) {
        return stateTree.data.products.get(productId + '');
      }
      return null;
    },

    get selectedVariation() {
      return !parseBoolean(enablevariations, true)
        ? state.data
        : ((state.data?.selectedVariation ?? state.data) as TypeProduct);
    },

    get priceSuffix() {
      if (!state.data) {
        return '';
      }
      if (state.data.variationTypeDescription) {
        return state.data.minimumUnitPurchase &&
          state.data.minimumUnitPurchase > 1
          ? `${state.data.minimumUnitPurchase}x ${state.data.variationTypeDescription}`
          : state.data.variationTypeDescription;
      } else {
        return state.data.minimumUnitPurchase &&
          state.data.minimumUnitPurchase > 1
          ? state.data.isMix
            ? 'Pack'
            : state.data.minimumUnitPurchase.toString()
          : state.data.isWine
          ? 'Bottle'
          : 'Each';
      }
    },

    get cartItem() {
      const item = stateTree.session?.cart.get(
        state.selectedVariation?.id + '',
      );
      return item ?? null;
    },

    get cartQuantity() {
      if (state.cartItem) {
        return state.cartItem.quantity;
      }
      return 0;
    },

    get isLiked() {
      return state.selectedVariation!.liked;
    },

    get isTried() {
      return state.selectedVariation!.tried;
    },

    get isSaleable() {
      return state.selectedVariation?.isSaleableWithStock ?? false;
    },

    get image() {
      return state.selectedVariation?.image ?? state.data?.image;
    },

    get showDisplayQty() {
      if (state.selectedVariation?.websiteProductStock) {
        return (
          state.isSaleable &&
          state.selectedVariation?.websiteProductStock.count <=
            state.selectedVariation.websiteProductStock.displayQuantityLeftAt &&
          state.selectedVariation.websiteProductStock.count > 0 &&
          !!state.selectedVariation.websiteProductStock.quantityLeft
        );
      }
      return state.isSaleable;
    },

    incrementCart() {
      stateTree.session?.addItem(state.selectedVariation!.id);
      this.performCartAction(true);
    },

    decrementCart() {
      if (state.cartItem) {
        stateTree.session?.removeItem(state.selectedVariation!.id);
        this.performCartAction(false);
      }
    },

    removeItem() {
      if (productId) {
        stateTree.session?.removeCartItem(productId);
      }
    },

    performCartAction(isIncrementCart: boolean) {
      if (cartaction === CartActionOption.ShowNotifications) {
        const parent = document.getElementById(
          state.selectedVariation!.id.toString(),
        );
        if (parent) {
          const btn = parent.querySelector('#btn-close') as HTMLElement;
          btn.click();
        }
        const header = isIncrementCart ? 'Added to cart' : 'Removed from cart';
        const message = isIncrementCart
          ? state.selectedVariation?.name
          : state.selectedVariation?.name;
        const subMessage = `${state.cartItem?.quantity} in cart`;
        showToast({
          id: state.selectedVariation!.id,
          header: header,
          message: message,
          actionLabel: 'View Cart',
          image: state.selectedVariation?.image,
          subMessage: subMessage,
          onClick: stateTree.widgets.cart.show,
          duration: Number(toasteroptions?.duration),
          toasterAnimation: toasteroptions?.toasteranimation,
        });
      } else if (cartaction === CartActionOption.ShowCart) {
        stateTree.widgets.cart.show();
      }
    },

    liked() {
      state.selectedVariation!.onLiked();
    },

    tried() {
      state.selectedVariation!.onTried();
    },
  }));

  const increment = useCallback(() => {
    if (overrideIncrement) {
      overrideIncrement();
    } else {
      state.incrementCart();
    }
  }, [state, overrideIncrement, state.incrementCart]);

  const decrement = useCallback(() => {
    if (overrideDecrement) {
      overrideDecrement();
    } else {
      state.decrementCart();
    }
  }, [state, overrideDecrement, state.decrementCart]);

  const fallbackImage = useFallbackImage(state.image);

  return { state, increment, decrement, fallbackImage };
};
